import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  contractualTerms,
  type ContractualTerm,
  type ContractualTermInput,
  type ContractualTerms,
  type OperationDetail,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DetailTabs } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { RichTextEditor, RichTextView } from '@/shared/ui/RichTextEditor';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { tituloDoTermo } from './cotacao';
import {
  chaveDosTermos,
  type PermissoesDoBloco,
  usePermissoesDosTermos,
  useTermosDaCotacao,
} from './termosDaCotacao';

/**
 * Um bloco da cotacao: "Habilitar na cotacao", os valores das TAGs e o texto, no
 * editor de texto rico com o contador de caracteres e palavras. Serve as abas do
 * editor "Objetos" e as clausulas particulares.
 *
 * So o que mudou vai no pedido. O texto do objeto e das condicoes muda; o da clausula
 * so na clausula em branco. O servidor saneia o HTML de novo e marca o texto que saiu
 * do copiado alem das TAGs.
 */
export function ContractualTermForm({
  operationId,
  termo,
  podeEditar,
  bloco,
  onSaved,
}: {
  operationId: string;
  termo: ContractualTerm;
  podeEditar: boolean;
  /** O que muda neste bloco; sem ele, tudo o que `podeEditar` libera. */
  bloco?: PermissoesDoBloco;
  onSaved?: (termos: ContractualTerms) => void;
}) {
  const podeTexto = podeEditar && (bloco?.texto ?? true);
  const podeHabilitar = podeEditar && (bloco?.habilitar ?? true);
  const podeTags = podeEditar && (bloco?.tags ?? true);
  const podeAlgo = podeTexto || podeHabilitar || podeTags;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [base, setBase] = useState(termo);
  const [corpo, setCorpo] = useState(termo.body);
  const [habilitado, setHabilitado] = useState(termo.enabled);
  const [valores, setValores] = useState<Record<string, string>>(termo.tag_values);
  // O bloco novo do servidor (depois de gravar) recompoe o formulario.
  if (base !== termo) {
    setBase(termo);
    setCorpo(termo.body);
    setHabilitado(termo.enabled);
    setValores(termo.tag_values);
  }

  const salvar = useMutation({
    mutationFn: (input: ContractualTermInput) =>
      contractualTerms.update(operationId, termo.id, input),
    onSuccess: (termos) => {
      toast.success('Texto da cotacao gravado.');
      queryClient.setQueryData(chaveDosTermos(operationId), termos);
      void queryClient.invalidateQueries({
        queryKey: ['operations', operationId],
        exact: true,
      });
      onSaved?.(termos);
    },
  });

  function mesmosValores(): boolean {
    const chaves = new Set([...Object.keys(valores), ...Object.keys(termo.tag_values)]);
    for (const chave of chaves) {
      if ((valores[chave] ?? '').trim() !== (termo.tag_values[chave] ?? ''))
        return false;
    }
    return true;
  }

  const pedido: ContractualTermInput = {};
  if (podeTexto && corpo !== termo.body && termo.body_editable) pedido.body = corpo;
  if (podeHabilitar && habilitado !== termo.enabled) pedido.enabled = habilitado;
  if (podeTags && !mesmosValores()) pedido.tag_values = valores;
  const semMudanca = Object.keys(pedido).length === 0;
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const titulo = tituloDoTermo(termo);

  return (
    <div className="flex flex-col gap-4">
      {salvar.error ? (
        <Alert tone="danger" title="Nao foi possivel gravar o texto">
          {salvar.error.message}
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink-800">
          <input
            type="checkbox"
            role="switch"
            aria-checked={habilitado}
            checked={habilitado}
            disabled={!podeHabilitar}
            onChange={(event) => setHabilitado(event.target.checked)}
            className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
          />
          Habilitar na cotacao
        </label>
        <div className="flex flex-wrap gap-1.5">
          {termo.template_version ? (
            <Badge tone="neutral">Versao {termo.template_version} do modelo</Badge>
          ) : null}
          {termo.insurer_reference ? (
            <Badge tone="neutral">Ref. {termo.insurer_reference}</Badge>
          ) : null}
          {termo.free_text ? <Badge tone="info">Clausula em branco</Badge> : null}
          {termo.modified ? (
            <Badge tone="warning">Texto alterado alem das TAGs</Badge>
          ) : null}
          {!termo.enabled ? <Badge tone="neutral">Fora da minuta</Badge> : null}
        </div>
      </div>

      {termo.tags.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {termo.tags.map((tag) =>
            tag.input_kind === 'editor_esteira' ? (
              <Textarea
                key={tag.tag_key}
                label={tag.label}
                help={false}
                value={valores[tag.tag_key] ?? ''}
                disabled={!podeTags}
                hint={`TAG {{${tag.tag_key}}}`}
                error={fieldErrorOf(apiError, `tag_values.${tag.tag_key}`)}
                onChange={(event) =>
                  setValores({ ...valores, [tag.tag_key]: event.target.value })
                }
              />
            ) : (
              <Field
                key={tag.tag_key}
                label={tag.label}
                help={false}
                value={valores[tag.tag_key] ?? ''}
                disabled={!podeTags}
                placeholder={tag.input_kind === 'data' ? 'dd/mm/aaaa' : undefined}
                hint={`TAG {{${tag.tag_key}}}`}
                error={fieldErrorOf(apiError, `tag_values.${tag.tag_key}`)}
                onChange={(event) =>
                  setValores({ ...valores, [tag.tag_key]: event.target.value })
                }
              />
            ),
          )}
        </div>
      ) : null}

      <RichTextEditor
        label={titulo}
        help={false}
        value={corpo}
        maxLength={termo.max_characters}
        disabled={!podeTexto || !termo.body_editable}
        rows={termo.kind === 'condicoes_gerais' ? 16 : 8}
        hint={
          !termo.body_editable
            ? 'O texto desta clausula vem do cadastro da modalidade; aqui mudam so as TAGs.'
            : podeEditar && !podeTexto
              ? 'Este texto e da seguradora: a corretora nao o altera.'
              : 'Editor otimizado para Word. As marcacoes {{...}} sao preenchidas na minuta.'
        }
        error={fieldErrorOf(apiError, 'body')}
        onChange={setCorpo}
      />

      {termo.original_body ? (
        <details className="rounded-md border border-line px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium text-ink-800">
            Dados do objeto da apolice inicial
          </summary>
          <p className="mt-1 text-2xs text-ink-500">
            O texto do modelo que a cotacao copiou, sem as alteracoes. So leitura.
          </p>
          <RichTextView html={termo.original_body} className="mt-2" />
        </details>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-2xs text-ink-500">
          {termo.updated_by_label
            ? `Alterado por ${termo.updated_by_label} em ${formatDateTime(termo.updated_at)}.`
            : `Copiado em ${formatDateTime(termo.created_at)}.`}
        </p>
        {podeAlgo ? (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={semMudanca || salvar.isPending}
              onClick={() => {
                setCorpo(termo.body);
                setHabilitado(termo.enabled);
                setValores(termo.tag_values);
              }}
            >
              Desfazer
            </Button>
            <Button
              loading={salvar.isPending}
              disabled={semMudanca}
              onClick={() => salvar.mutate(pedido)}
            >
              Salvar texto
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Secao "Objetos" do Resumo da cotacao (COT-069 a COT-077): uma aba por bloco, na
 * ordem do OnPoint (Objeto, Objeto Cobertura Adicional, Condicoes Gerais, Condicoes
 * Especiais Cobertura Adicional), cada uma com "Habilitar na cotacao", as TAGs e o
 * editor rico.
 *
 * Os textos sao copia da versao em vigor da modalidade (P-PAR-2). "Carregar textos da
 * modalidade" traz o que foi ativado depois da abertura, sem escrever por cima.
 */
export function ContractualTermsEditor({ operacao }: { operacao: OperationDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const consulta = useTermosDaCotacao(operacao.id);
  const pode = usePermissoesDosTermos(operacao, consulta.data);
  const [aba, setAba] = useState<string>('');

  const carregar = useMutation({
    mutationFn: () => contractualTerms.assemble(operacao.id),
    onSuccess: (termos) => {
      const antes = blocosDoEditor(consulta.data).length;
      const depois = blocosDoEditor(termos).length;
      toast.success(
        depois > antes
          ? `${depois - antes} texto(s) da modalidade carregado(s).`
          : 'A cotacao ja tem todos os textos em vigor da modalidade.',
      );
      queryClient.setQueryData(chaveDosTermos(operacao.id), termos);
      void queryClient.invalidateQueries({
        queryKey: ['operations', operacao.id],
        exact: true,
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const condicionada = useMutation({
    mutationFn: (ligada: boolean) =>
      contractualTerms.setConditionalDraft(operacao.id, ligada),
    onSuccess: (termos) => {
      toast.success('Minuta condicionada gravada.');
      queryClient.setQueryData(chaveDosTermos(operacao.id), termos);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const blocos = blocosDoEditor(consulta.data);
  const ativo = blocos.find((termo) => termo.id === aba) ?? blocos[0];

  return (
    <Card>
      <CardHeader
        title="Objetos"
        description="Objeto e condicoes copiados da versao em vigor da modalidade. O bloco com Habilitar na cotacao desligado nao sai na minuta."
        action={
          pode.montar ? (
            <Button
              variant="secondary"
              loading={carregar.isPending}
              onClick={() => carregar.mutate()}
            >
              Carregar textos da modalidade
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-4">
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os textos da cotacao">
            {consulta.error.message}
          </Alert>
        ) : null}

        {consulta.data?.complementary_object_missing ? (
          <Alert tone="info" title="Objeto da modalidade complementar nao cadastrado.">
            A modalidade complementar da cotacao nao tem objeto em vigor. Ative o objeto
            dela em Parametrizacao e carregue os textos da modalidade aqui.
          </Alert>
        ) : null}

        {consulta.data?.object_modified ? (
          <Alert tone="warning" title="Objeto totalmente modificado">
            O objeto saiu do texto da modalidade alem das TAGs. O que essa alteracao
            exige (SUB-011) ainda nao foi homologado: a cotacao so fica marcada.
          </Alert>
        ) : null}

        {consulta.isPending ? (
          <p className="text-sm text-ink-500">Carregando os textos.</p>
        ) : !ativo ? (
          <EmptyState
            icon="file"
            title="Nenhum texto da modalidade nesta cotacao"
            description="A cotacao copia o objeto e as condicoes em vigor da modalidade. Ative os textos em Parametrizacao e carregue-os aqui."
          />
        ) : (
          <>
            <DetailTabs
              label="Blocos do objeto e das condicoes"
              tabs={blocos.map((termo) => ({
                key: termo.id,
                title: tituloDoTermo(termo),
              }))}
              active={ativo.id}
              onChange={setAba}
            />
            <ContractualTermForm
              key={ativo.id}
              operationId={operacao.id}
              termo={ativo}
              podeEditar={pode.textos}
              bloco={pode.doBloco(ativo)}
            />
          </>
        )}

        {consulta.data ? (
          <label className="flex cursor-pointer items-start gap-2 border-t border-line pt-3 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={consulta.data.conditional_draft_enabled}
              disabled={!pode.montar || condicionada.isPending}
              onChange={(event) => condicionada.mutate(event.target.checked)}
              className="mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            <span>
              Disponibilizar minuta condicionada
              <span className="block text-2xs text-ink-500">
                Guardado sem efeito: o que a liberacao condicionada da minuta faz ainda
                nao tem fonte (COT-200).
              </span>
            </span>
          </label>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** Os blocos do editor "Objetos": tudo menos as clausulas particulares. */
function blocosDoEditor(termos: ContractualTerms | undefined): ContractualTerm[] {
  return (termos?.items ?? []).filter((termo) => termo.kind !== 'clausula_particular');
}
